import type {
  ChartType,
  ChartConfig,
  MetricKey,
  ChartMode,
  AggregationBy,
  ChartOptions,
} from '../type/dynamicChart';
import { chartTypeRegistry } from '../registry/chartTypes';
import { metricRegistry } from '../registry/metrics';

/**
 * Check if a metric is valid for the given mode
 */
function isMetricValidForMode(metric: MetricKey | undefined, mode: ChartMode): boolean {
  if (!metric) return false;

  // 'reference' is only valid in comparison mode
  if (metric === 'reference') {
    return mode === 'comparison';
  }

  return true;
}

/**
 * Check if an aggregation level is valid for the given mode
 */
function isAggregationValidForMode(
  aggregation: AggregationBy | undefined,
  mode: ChartMode
): boolean {
  if (!aggregation) return false;

  // 'reference' is only valid in comparison mode
  if (aggregation === 'reference') {
    return mode === 'comparison';
  }

  return true;
}

/**
 * Build default ChartOptions from the registry's optionFields for a given chart type.
 * Checkbox defaults become boolean values; select defaults become strings (or numbers for binCount);
 * number-input defaults become numbers.
 */
function buildDefaultOptionsFromRegistry(chartType: ChartType): Partial<ChartOptions> {
  const typeDef = chartTypeRegistry.find((ct) => ct.key === chartType);
  if (!typeDef) return {};

  const opts: Record<string, unknown> = {};
  for (const field of typeDef.optionFields) {
    if (field.kind === 'checkbox') {
      opts[field.key] = field.default;
    } else if (field.kind === 'number-input') {
      opts[field.key] = field.default;
    } else if (field.kind === 'select') {
      // binCount is stored as a number in ChartOptions
      opts[field.key] = field.key === 'binCount' ? parseInt(field.default, 10) : field.default;
    }
  }
  // Type assertion is safe: we build from known ChartOptions keys via registry
  return opts as Partial<ChartOptions>;
}

/**
 * Get default configuration values for a specific chart type
 * Ensures no blank fields when switching chart types
 * Mode-aware: returns appropriate defaults for analytics vs comparison mode
 * Options are now derived from the chartTypeRegistry's optionFields.
 */
export function getDefaultConfigForChartType(
  chartType: ChartType,
  mode: ChartMode = 'analytics'
): Partial<ChartConfig> {
  // Mode-appropriate base defaults
  const baseOptions: Partial<ChartOptions> = {
    showGridLines: true,
    showDataLabels: false,
    colorBy: mode === 'analytics' ? 'toolId' : 'reference',
  };

  const baseDefaults = {
    aggregatedBy: (mode === 'analytics' ? 'tool' : 'reference') as AggregationBy,
    options: baseOptions,
  };

  // Default X-axis metric based on mode
  const defaultXMetric: MetricKey = mode === 'analytics' ? 'toolId' : 'reference';
  const defaultXLabel = getMetricLabelKey(defaultXMetric);

  // Registry-driven option defaults
  const registryOptions = buildDefaultOptionsFromRegistry(chartType);

  // bar chart in comparison mode gets a groupBy default (context-specific, not in registry)
  const extraOptions: Partial<ChartOptions> =
    chartType === 'bar' && mode === 'comparison' ? { groupBy: 'reference' as const } : {};

  return {
    ...baseDefaults,
    layout: { size: 'md' as const, order: 0 },
    xAxis: { metric: defaultXMetric, label: defaultXLabel },
    yAxis: {
      metric: 'totalMachiningDuration' as MetricKey,
      label: getMetricLabelKey('totalMachiningDuration'),
      aggregation: metricRegistry.find((m) => m.key === 'totalMachiningDuration')?.defaultAggregation ?? 'avg',
    },
    options: {
      ...baseDefaults.options,
      ...registryOptions,
      ...extraOptions,
    },
  };
}

/**
 * Label of a metric, stored in a chart's axis config: a translation key, translated by the
 * component that displays it.
 */
export function getMetricLabelKey(metric: MetricKey): string {
  return `analytics.dashboard.complexCharts.configuration.metrics.${metric}`;
}

/**
 * Minimal shape needed from a metric registry entry for stale-metric detection.
 */
type MetricRegistryEntry = { key: string };

/**
 * Validate and fix chart configuration
 * Ensures all required fields have valid values
 * Mode-aware: replaces invalid values with mode-appropriate defaults
 *
 * @param staleMetricRegistry - Optional registry override; when provided, validates that axis
 *   metrics exist. If a metric is not found, sets `invalidReason` instead of substituting.
 */
export function validateChartConfig(
  config: ChartConfig,
  mode: ChartMode = 'analytics',
  staleMetricRegistry?: MetricRegistryEntry[]
): ChartConfig {
  const defaults = getDefaultConfigForChartType(config.type, mode);

  // Validate and fix xAxis metric if invalid for current mode
  const xMetric = isMetricValidForMode(config.xAxis?.metric, mode)
    ? config.xAxis.metric
    : defaults.xAxis!.metric;

  // Ensure xAxis has valid metric and label
  const xAxis = {
    metric: xMetric,
    label: config.xAxis?.label || getMetricLabelKey(xMetric),
    aggregation: config.xAxis?.aggregation,
  };

  // Reset X-axis to categorical default if chart type only supports categorical X
  const xMetricDef = metricRegistry.find((m) => m.key === xAxis.metric);
  const xMetricKind = xMetricDef?.kind ?? null;
  const chartTypeDef = chartTypeRegistry.find((ct) => ct.key === config.type);
  const typeSupportedXKinds = chartTypeDef?.supportedXKinds ?? ['categorical'];
  if (xMetricKind !== null && !typeSupportedXKinds.includes(xMetricKind)) {
    xAxis.metric = defaults.xAxis!.metric;
    xAxis.label = getMetricLabelKey(defaults.xAxis!.metric);
  }

  // Ensure yAxis has valid metric and label (yAxis doesn't need mode validation)
  const yMetric = config.yAxis?.metric || defaults.yAxis!.metric;
  const yMetricDef = metricRegistry.find((m) => m.key === yMetric);
  const yAggregation =
    config.yAxis?.aggregation && yMetricDef?.supportedAggregations.includes(config.yAxis.aggregation)
      ? config.yAxis.aggregation
      : yMetricDef?.defaultAggregation;
  const yAxis = {
    metric: yMetric,
    label: config.yAxis?.label || getMetricLabelKey(yMetric),
    aggregation: yAggregation,
  };

  // Validate and fix aggregatedBy if invalid for current mode
  const aggregatedBy = isAggregationValidForMode(config.aggregatedBy, mode)
    ? config.aggregatedBy
    : defaults.aggregatedBy!;

  // Validate and fix colorBy if invalid for current mode
  const colorBy =
    config.options?.colorBy && isMetricValidForMode(config.options.colorBy, mode)
      ? config.options.colorBy
      : defaults.options!.colorBy;

  // Merge options with defaults, ensuring colorBy is valid
  const options = {
    ...defaults.options,
    ...config.options,
    colorBy,
  };

  // Stale-metric detection — only when a registry override is provided
  let invalidReason: string | undefined = undefined;
  if (staleMetricRegistry) {
    const registryKeys = staleMetricRegistry.map((m) => m.key);
    if (!registryKeys.includes(xAxis.metric)) {
      invalidReason = `xAxis.metric "${xAxis.metric}" not found in metricRegistry`;
    } else if (!registryKeys.includes(yAxis.metric)) {
      invalidReason = `yAxis.metric "${yAxis.metric}" not found in metricRegistry`;
    }
  }

  return {
    ...config,
    xAxis,
    yAxis,
    aggregatedBy,
    options,
    invalidReason,
    layout: config.layout ?? { size: 'md' as const, order: 0 },
  };
}
